import { Controller } from "@hotwired/stimulus"
import * as serverTime from "../lib/server_time"

const ALLOWED_EXTENSIONS = [
  "jpg","jpeg","gif","png","dng","heic","heif","webp","tiff","bmp",
  "doc","docx","pdf","xml","txt","xls","xlsx","xlsm","zip","rar","dwg","pptx","ppt"
]
const MAX_UPLOAD_SIZE = 250 * 1024 * 1024

export default class extends Controller {
  static values = {
    projectId: Number,
    docMode: String,         // "bid" or "addenda"
    presignUrl: String,
    registerUrl: String,
    submitUrl: String,
    typeOptions: Array,      // [[name, id], ...]
    isOwner: Boolean,
    showAddendaModal: Boolean,
    whoPlacedBid: Number,
    currentUserId: Number,
    publicUrl: String,
    deadlineUtc: String,
    scheduleTbd: Boolean,
    exportUrl: String,
    deleteBaseUrl: String,
    serverNow: { type: Number, default: 0 },
    i18n: { type: Object, default: {} }  // pages.bid_docs_js — falls back to the English below
  }

  static targets = [
    "uploadModal", "dropzone", "fileInput", "fileChips",
    "typeAssignList", "submitBtn", "notifyCheckbox", "notifyRow", "actionRow",
    "countdown", "addendaWarningModal", "invalidBidModal",
    "groupContainer", "addendFilenameWarning",
    "deleteConfirmModal", "deleteConfirmFileName",
    "exportModal", "exportCheckbox", "exportSubmitBtn"
  ]

  connect() {
    this._uploadedFiles = []   // { id, filename, chipEl }
    this._pendingUploads = 0
    this._countdownInterval = null
    if (this.serverNowValue) serverTime.seed(this.serverNowValue)
    if (this.currentUserIdValue) serverTime.subscribe(this.currentUserIdValue)
    this.startCountdown()

    // Re-sync countdown immediately when user returns to this tab/window
    this._onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        serverTime.requestSync()
        this._updateCountdown()
      }
    }
    document.addEventListener("visibilitychange", this._onVisibilityChange)

    if (this.showAddendaModalValue && this.hasInvalidBidModalTarget) {
      this.invalidBidModalTarget.classList.remove("hidden")
    }
  }

  disconnect() {
    if (this._countdownInterval) clearInterval(this._countdownInterval)
    if (this._onVisibilityChange) document.removeEventListener("visibilitychange", this._onVisibilityChange)
    if (this.currentUserIdValue) serverTime.unsubscribe()
  }

  // ── Upload Modal ─────────────────────────────────────────────────────

  openUpload() {
    this._uploadedFiles = []
    this._pendingUploads = 0
    if (this.hasFileChipsTarget) this.fileChipsTarget.innerHTML = ""
    if (this.hasTypeAssignListTarget) this.typeAssignListTarget.innerHTML = ""
    if (this.hasNotifyRowTarget) this.notifyRowTarget.classList.add("hidden")
    if (this.hasActionRowTarget) this.actionRowTarget.classList.add("hidden")
    this.uploadModalTarget.classList.remove("hidden")
  }

  closeUpload() {
    this.uploadModalTarget.classList.add("hidden")
  }

  triggerFileInput(e) {
    // Don't trigger if clicking inside the file input itself
    if (e.target === this.fileInputTarget) return
    this.fileInputTarget.click()
  }

  fileInputChanged() {
    const files = this.fileInputTarget.files
    if (files.length > 0) this._processFiles(Array.from(files))
    this.fileInputTarget.value = ""
  }

  dropOnZone(e) {
    e.preventDefault()
    this.dropzoneTarget.classList.remove("border-brand", "bg-brand/5")
    const files = e.dataTransfer.files
    if (files.length > 0) this._processFiles(Array.from(files))
  }

  dragOverZone(e) {
    e.preventDefault()
    this.dropzoneTarget.classList.add("border-brand", "bg-brand/5")
  }

  dragLeaveZone(e) {
    e.preventDefault()
    this.dropzoneTarget.classList.remove("border-brand", "bg-brand/5")
  }

  // ── Upload Flow (chip-based, parallel S3 uploads) ──────────────────────

  async _processFiles(files) {
    // Validate
    for (const f of files) {
      const ext = f.name.split(".").pop().toLowerCase()
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        alert(this._t("file_type_not_allowed", "File type .%{ext} is not allowed.", { ext }))
        return
      }
      if (f.size > MAX_UPLOAD_SIZE) {
        alert(this._t("file_too_big", "%{name} exceeds the 250MB limit.", { name: f.name }))
        return
      }
    }

    // Create blue uploading chips immediately
    const entries = files.map(f => {
      const chip = this._createChip(f.name, { uploading: true })
      this.fileChipsTarget.appendChild(chip)
      return { file: f, chip }
    })

    this._pendingUploads++
    this._updateSubmitState()

    try {
      // 1. Get presigned URLs
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        body: JSON.stringify({
          files: entries.map(({ file }) => ({
            filename: file.name,
            content_type: file.type || "application/octet-stream",
            byte_size: file.size
          }))
        })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || this._t("prepare_failed", "Failed to prepare upload."))
      }

      const { uploads } = await presignRes.json()

      // 2. Upload all files to S3 in parallel
      const results = await Promise.allSettled(
        uploads.map(async (upload, i) => {
          const file = entries[i].file
          const res = await fetch(upload.upload_url, {
            method: "PUT",
            headers: { "Content-Type": file.type || upload.content_type },
            body: file
          })
          if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)
          return upload
        })
      )

      // Remove chips for failed uploads
      const successful = []
      results.forEach((r, i) => {
        if (r.status === "fulfilled") {
          successful.push({ upload: r.value, entry: entries[i] })
        } else {
          entries[i].chip.remove()
        }
      })

      if (successful.length === 0) throw new Error(this._t("all_uploads_failed", "All uploads failed"))

      // 3. Register with backend
      const registerRes = await fetch(this.registerUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        body: JSON.stringify({
          files: successful.map(({ upload }) => ({
            cache_name: upload.cache_name,
            filename: upload.filename
          }))
        })
      })

      if (!registerRes.ok) throw new Error(this._t("register_failed", "Failed to register files."))
      const registered = await registerRes.json()

      // 4. Replace blue chips with gray done chips + add type assignment rows
      registered.forEach((reg, i) => {
        const { entry } = successful[i]
        // Remove uploading chip
        entry.chip.remove()

        // Create gray done chip
        const doneChip = this._createChip(reg.filename, {
          removable: true,
          onRemove: () => this._removeFile(reg.id, doneChip)
        })
        this.fileChipsTarget.appendChild(doneChip)

        // Track uploaded file
        this._uploadedFiles.push({ id: reg.id, filename: reg.filename, chipEl: doneChip })

        // Add type assignment row
        this._addTypeAssignRow(reg)
      })

      // Show notification + action rows
      this._showAssignmentUI()
    } catch (e) {
      console.error("Upload failed:", e)
      entries.forEach(({ chip }) => chip.remove())
      alert(e.message || this._t("upload_failed", "Upload failed. Please try again."))
    } finally {
      this._pendingUploads--
      this._updateSubmitState()
    }
  }

  _removeFile(docId, chipEl) {
    chipEl.remove()
    this._uploadedFiles = this._uploadedFiles.filter(f => f.id !== docId)
    // Remove matching type assignment row
    const row = this.typeAssignListTarget.querySelector(`[data-doc-id="${docId}"]`)
    if (row) row.remove()
    // Hide UI if no files remain
    if (this._uploadedFiles.length === 0) {
      if (this.hasNotifyRowTarget) this.notifyRowTarget.classList.add("hidden")
      if (this.hasActionRowTarget) this.actionRowTarget.classList.add("hidden")
    }
  }

  _addTypeAssignRow(reg) {
    const optionsHtml = this.typeOptionsValue.map(
      ([name, id]) => `<option value="${id}">${this._escapeHtml(name)}</option>`
    ).join("")

    const row = document.createElement("div")
    row.className = "flex items-center gap-3 py-2 border-b border-gray-100"
    row.dataset.docId = reg.id
    row.innerHTML = `
      <span class="truncate text-[13px] text-gray-700 flex-1">${this._escapeHtml(reg.filename)}</span>
      <select class="rounded border-gray-300 text-[13px] py-1 px-2" data-doc-select="${reg.id}">
        <option value="" disabled selected>${this._escapeHtml(this._t("select_type", "Select type..."))}</option>
        ${optionsHtml}
      </select>
    `
    const select = row.querySelector("select")
    select.addEventListener("change", () => {
      if (select.value) {
        select.classList.remove("border-red-400")
        select.classList.add("border-gray-300")
      }
    })

    this.typeAssignListTarget.appendChild(row)
  }

  _showAssignmentUI() {
    if (this._uploadedFiles.length > 0) {
      if (this.hasNotifyRowTarget) {
        this.notifyRowTarget.classList.remove("hidden")
        this.notifyRowTarget.classList.add("flex")
      }
      if (this.hasActionRowTarget) {
        this.actionRowTarget.classList.remove("hidden")
        this.actionRowTarget.classList.add("flex")
      }
    }
  }

  _updateSubmitState() {
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = this._pendingUploads > 0
    }
  }

  // ── Chip Creation ──────────────────────────────────────────────────────

  _createChip(name, { uploading = false, removable = false, onRemove = null } = {}) {
    const chip = document.createElement("div")
    chip.className = `inline-flex items-center gap-1.5 pl-2 ${removable ? "pr-1" : "pr-2"} py-1 rounded-md text-xs`

    chip.classList.add(uploading ? "chip-uploading" : "chip-file")

    const icon = uploading
      ? `<svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>`
      : `<svg class="w-3.5 h-3.5 shrink-0 chip-file-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>`

    const truncatedName = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name

    let removeBtn = ""
    if (removable) {
      removeBtn = `<button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-300/50 text-gray-400 hover:text-gray-600 transition-colors" data-remove-chip>
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`
    }

    chip.innerHTML = `${icon}<span class="truncate max-w-[180px]" title="${this._escapeHtml(name)}">${this._escapeHtml(truncatedName)}</span>${removeBtn}`

    if (removable && onRemove) {
      const btn = chip.querySelector("[data-remove-chip]")
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        onRemove()
      })
    }

    return chip
  }

  // ── Type Assignment + Submit ────────────────────────────────────────────

  submitAssignments() {
    const assignments = this._uploadedFiles.map(f => {
      const select = this.element.querySelector(`[data-doc-select="${f.id}"]`)
      return { id: f.id, type_id: select ? select.value : null }
    })

    // Validate all files have a type selected
    const missing = assignments.some(a => !a.type_id)
    if (missing) {
      this._uploadedFiles.forEach(f => {
        const select = this.element.querySelector(`[data-doc-select="${f.id}"]`)
        if (select && !select.value) {
          select.classList.add("border-red-400")
          select.classList.remove("border-gray-300")
        }
      })
      this._showToast(this._t("select_type_for_all", "Please select a type for all documents."), "error")
      return
    }

    // Check for "addend" in filename when in bid mode
    if (this.docModeValue === "bid") {
      const hasAddendaFile = this._uploadedFiles.some(f =>
        f.filename.toLowerCase().includes("addend")
      )
      if (hasAddendaFile && this.hasAddendFilenameWarningTarget) {
        this._pendingAssignments = assignments
        this.addendFilenameWarningTarget.classList.remove("hidden")
        return
      }
    }

    this._doSubmitAssignments(assignments)
  }

  confirmAddendaWarning() {
    if (this.hasAddendFilenameWarningTarget) {
      this.addendFilenameWarningTarget.classList.add("hidden")
    }
    if (this._pendingAssignments) {
      this._doSubmitAssignments(this._pendingAssignments)
      this._pendingAssignments = null
    }
  }

  cancelAddendaWarning() {
    if (this.hasAddendFilenameWarningTarget) {
      this.addendFilenameWarningTarget.classList.add("hidden")
    }
    this._pendingAssignments = null
  }

  async _doSubmitAssignments(assignments) {
    const sendNotification = this.hasNotifyCheckboxTarget ? this.notifyCheckboxTarget.checked : false

    const data = assignments.map(a => `${a.id}|${a.type_id}`).join(",")

    const form = document.createElement("form")
    form.method = "POST"
    form.action = this.submitUrlValue

    const csrfInput = document.createElement("input")
    csrfInput.type = "hidden"
    csrfInput.name = "authenticity_token"
    csrfInput.value = this._csrfToken()
    form.appendChild(csrfInput)

    const dataInput = document.createElement("input")
    dataInput.type = "hidden"
    dataInput.name = "data"
    dataInput.value = data
    form.appendChild(dataInput)

    if (sendNotification) {
      const notifKey = this.docModeValue === "bid" ? "send_bid_docs_notification" : "send_addenda_docs_notification"
      const notifInput = document.createElement("input")
      notifInput.type = "hidden"
      notifInput.name = notifKey
      notifInput.value = "1"
      form.appendChild(notifInput)
    }

    const projectInput = document.createElement("input")
    projectInput.type = "hidden"
    projectInput.name = "project_id"
    projectInput.value = this.projectIdValue
    form.appendChild(projectInput)

    document.body.appendChild(form)
    form.submit()
  }

  // ── Delete ───────────────────────────────────────────────────────────

  deleteDocument(e) {
    this._pendingDeleteDocId = e.currentTarget.dataset.docId
    this._pendingDeleteRow = e.currentTarget.closest("[data-doc-row]")

    // Show filename in modal
    const fileName = this._pendingDeleteRow?.querySelector("a")?.textContent?.trim() || "this document"
    this.deleteConfirmFileNameTarget.textContent = fileName

    this.deleteConfirmModalTarget.classList.remove("hidden")
  }

  cancelDelete() {
    this._pendingDeleteDocId = null
    this._pendingDeleteRow = null
    this.deleteConfirmModalTarget.classList.add("hidden")
  }

  async confirmDelete() {
    const docId = this._pendingDeleteDocId
    const row = this._pendingDeleteRow
    this.deleteConfirmModalTarget.classList.add("hidden")

    if (!docId) return

    const url = `${this.deleteBaseUrlValue}/${docId}`
    try {
      const resp = await fetch(url, {
        method: "DELETE",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      if (resp.ok) {
        if (row) {
          row.style.transition = "opacity 0.3s"
          row.style.opacity = "0"
          setTimeout(() => {
            row.remove()
            this.element.querySelectorAll("[data-group-body]").forEach(body => {
              if (body.querySelectorAll("[data-doc-row]").length === 0) {
                const group = body.closest("[data-group-container]")
                if (group) group.remove()
              }
            })
          }, 300)
        }
        this._showToast(this._t("document_deleted", "Document deleted."))
      } else {
        this._showToast(this._t("delete_failed", "Failed to delete document."), "error")
      }
    } catch {
      this._showToast(this._t("delete_failed", "Failed to delete document."), "error")
    } finally {
      this._pendingDeleteDocId = null
      this._pendingDeleteRow = null
    }
  }

  // ── Export ───────────────────────────────────────────────────────────

  openExportModal() {
    this.exportModalTarget.classList.remove("hidden")
  }

  closeExportModal() {
    this.exportModalTarget.classList.add("hidden")
  }

  async submitExport() {
    const checked = this.exportCheckboxTargets.filter(cb => cb.checked)
    const categoryIds = checked.map(cb => parseInt(cb.value)).filter(id => !isNaN(id))

    if (categoryIds.length === 0) {
      this._showToast(this._t("select_one_section", "Select at least one section"), "error")
      return
    }

    this.exportSubmitBtnTarget.disabled = true
    try {
      const resp = await fetch(this.exportUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json"
        },
        body: JSON.stringify({ category_ids: categoryIds })
      })

      if (resp.ok) {
        this.closeExportModal()
        this._showToast(this._t("export_queued", "Export queued. You\u2019ll receive an email when ready."))
      } else {
        this._showToast(this._t("export_failed", "Export failed. Please try again."), "error")
      }
    } catch {
      this._showToast(this._t("export_failed", "Export failed. Please try again."), "error")
    } finally {
      this.exportSubmitBtnTarget.disabled = false
    }
  }

  // ── Clipboard ────────────────────────────────────────────────────────

  async copyPublicLink() {
    if (!this.publicUrlValue) return
    try {
      await navigator.clipboard.writeText(this.publicUrlValue)
      const btn = this.element.querySelector("[data-copy-btn]")
      if (btn) {
        const original = btn.innerHTML
        btn.innerHTML = `<span class="text-[13px] text-green-600">${this._escapeHtml(this._t("copied", "Copied!"))}</span>`
        setTimeout(() => { btn.innerHTML = original }, 2000)
      }
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea")
      ta.value = this.publicUrlValue
      document.body.appendChild(ta)
      ta.select()
      document.execCommand("copy")
      ta.remove()
    }
  }

  // ── Countdown Timer ──────────────────────────────────────────────────

  startCountdown() {
    if (!this.hasCountdownTarget) return
    if (this.scheduleTbdValue) {
      this.countdownTarget.textContent = "TBD"
      return
    }
    if (!this.deadlineUtcValue) return

    this._updateCountdown()
    this._countdownInterval = setInterval(() => this._updateCountdown(), 1000)
  }

  _updateCountdown() {
    const utcStr = this.deadlineUtcValue
    if (!utcStr) return

    const deadline = new Date(utcStr)
    const now = serverTime.now()
    if (!now) return // Server time not yet available
    const diff = deadline - now

    if (diff <= 0) {
      this.countdownTarget.textContent = this._t("ended", "Ended")
      if (this._countdownInterval) clearInterval(this._countdownInterval)
      return
    }

    const days = Math.floor(diff / 86400000)
    const hrs = Math.floor((diff % 86400000) / 3600000)
    const mins = Math.floor((diff % 3600000) / 60000)
    const secs = Math.floor((diff % 60000) / 1000)

    const pad = n => String(n).padStart(2, "0")
    this.countdownTarget.textContent = days > 0
      ? `${days} ${this._t("days", "days")} ${pad(hrs)}:${pad(mins)}:${pad(secs)}`
      : `${pad(hrs)}:${pad(mins)}:${pad(secs)}`
  }

  // Group collapse removed — all documents are always visible

  // ── Invalid Bid Modal ────────────────────────────────────────────────

  closeInvalidBidModal() {
    if (this.hasInvalidBidModalTarget) {
      this.invalidBidModalTarget.classList.add("hidden")
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  _t(key, fallback, vars = {}) {
    const text = (this.i18nValue && this.i18nValue[key]) || fallback
    return text.replace(/%\{(\w+)\}/g, (_, name) => (vars[name] ?? ""))
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
